/**
 * Waveform overview of the wizard's song (W2-14 onboarding-wizard; DESIGN.md §4.3 "[waveform ...] [play]"):
 * min / max per column of the mono mix, an optional highlighted range (the song-select preview) and the playback
 * cursor while the song plays. Colours come from the design tokens (`--color-curve`, `--color-accent-muted`,
 * `--color-playhead`); a click reports the song time under the pointer (`onPick`).
 */

import { useEffect, useMemo, useRef } from 'react';
import { waveformPeaks, type DecodedSong } from './songAnalysis';
import s from './Wizard.module.css';

export interface WaveformProps {
  readonly song: DecodedSong;
  /** Accessible description ("Waveform of song.mp3, 3:12"). */
  readonly label: string;
  readonly range?: { readonly start: number; readonly end: number } | null;
  /** Song time of the playback (polled every frame while `active`). */
  readonly position?: () => number | null;
  readonly active?: boolean;
  /** Click: the song time under the pointer (a mouse shortcut; the fields next to it are the keyboard path). */
  readonly onPick?: (sec: number) => void;
}

const COLUMNS = 600;

function cssVar(el: Element, name: string): string {
  const v = getComputedStyle(el).getPropertyValue(name).trim();
  return v === '' ? getComputedStyle(el).color : v;
}

export function Waveform(props: WaveformProps) {
  const { song, range, position, active, onPick } = props;
  const canvas = useRef<HTMLCanvasElement>(null);
  const peaks = useMemo(() => waveformPeaks(song, COLUMNS), [song]);

  useEffect(() => {
    const el = canvas.current;
    if (el === null) return undefined;
    let frame = 0;
    const draw = (): void => {
      const ctx = el.getContext('2d');
      if (ctx === null) return;
      const dpr = typeof devicePixelRatio === 'number' && devicePixelRatio > 0 ? devicePixelRatio : 1;
      const w = Math.max(1, Math.round(el.clientWidth * dpr));
      const h = Math.max(1, Math.round(el.clientHeight * dpr));
      if (el.width !== w) el.width = w;
      if (el.height !== h) el.height = h;
      ctx.clearRect(0, 0, w, h);
      const dur = song.durationSec > 0 ? song.durationSec : 1;
      if (range !== undefined && range !== null && range.end > range.start) {
        ctx.fillStyle = cssVar(el, '--color-accent-muted');
        const x0 = (range.start / dur) * w;
        const x1 = (range.end / dur) * w;
        ctx.fillRect(x0, 0, Math.max(1, x1 - x0), h);
      }
      ctx.fillStyle = cssVar(el, '--color-curve');
      const n = peaks.length / 2;
      const colW = w / n;
      for (let i = 0; i < n; i++) {
        const lo = peaks[i * 2] ?? 0;
        const hi = peaks[i * 2 + 1] ?? 0;
        const y0 = ((1 - hi) / 2) * h;
        const y1 = ((1 - lo) / 2) * h;
        ctx.fillRect(i * colW, y0, Math.max(1, colW - 0.5), Math.max(1, y1 - y0));
      }
      const pos = position?.() ?? null;
      if (pos !== null) {
        ctx.fillStyle = cssVar(el, '--color-playhead');
        ctx.fillRect((pos / dur) * w - dpr, 0, 2 * dpr, h);
      }
      if (active === true) frame = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [peaks, song, range, position, active]);

  const pick = (clientX: number): void => {
    const el = canvas.current;
    if (el === null || onPick === undefined) return;
    const rect = el.getBoundingClientRect();
    const f = rect.width > 0 ? (clientX - rect.left) / rect.width : 0;
    onPick(Math.max(0, Math.min(1, f)) * song.durationSec);
  };

  return (
    <canvas
      ref={canvas}
      className={s.waveform}
      role="img"
      aria-label={props.label}
      data-clickable={onPick === undefined ? undefined : 'true'}
      onClick={(e) => {
        pick(e.clientX);
      }}
    />
  );
}
